import { useCallback, useMemo } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { AccountBalance, Business } from '@mui/icons-material';
import { Button, Chip, Typography } from '@mui/material';

import { QUEUE_VIEW_KEYS, WORK_VIEW_LABELS } from '../../billing/types/encounterWork';
import { BackButton } from '../components/back-button';
import { FilterableDataTable } from '../components/filterable-data-table';
import { useBillingDashboardOrgsQuery } from '../store/hooks';
import type { OrganizationBillingSummary } from '../types';
import { adminClaimWorkUrl } from '../utils/claimWorkNavigation';
import { formatCents, formatPercent } from '../utils/formatting';

export default function BillingDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = parseInt(searchParams.get('page') || '1', 10);
  const perPage = parseInt(searchParams.get('perPage') || '20', 10);
  const search = searchParams.get('search') || undefined;
  const sortBy = searchParams.get('sortBy') || 'claims_fix';
  const sortOrder = (searchParams.get('sortOrder') || 'desc') as 'asc' | 'desc';

  const { data, isLoading } = useBillingDashboardOrgsQuery({
    page,
    perPage,
    search,
    sortBy,
    sortDir: sortOrder,
  });

  const updateParams = useCallback(
    (updates: Record<string, string | undefined>) => {
      const params = new URLSearchParams(searchParams);
      for (const [key, value] of Object.entries(updates)) {
        if (value) {
          params.set(key, value);
        } else {
          params.delete(key);
        }
      }
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const handlePageChange = useCallback(
    (newPage: number) => updateParams({ page: newPage.toString() }),
    [updateParams],
  );

  const handlePerPageChange = useCallback(
    (newPerPage: number) => updateParams({ perPage: newPerPage.toString(), page: '1' }),
    [updateParams],
  );

  const handleFiltersChange = useCallback(
    (filters: Record<string, string>) => {
      updateParams({
        page: '1',
        search: filters.search || undefined,
      });
    },
    [updateParams],
  );

  const handleSortChange = useCallback(
    (newSortBy: string, newSortOrder: 'asc' | 'desc') => {
      updateParams({ sortBy: newSortBy, sortOrder: newSortOrder });
    },
    [updateParams],
  );

  const columns = useMemo(
    () => [
      {
        key: 'organization_name' as const,
        label: 'Organization',
        render: (value: unknown) => (
          <div className="flex items-center">
            <Business className="h-4 w-4 text-gray-400 mr-2" />
            <span className="font-medium text-gray-900">
              {typeof value === 'string' ? value : 'Unnamed'}
            </span>
          </div>
        ),
      },
      ...QUEUE_VIEW_KEYS.map(view => ({
        key: `claims_${view}` as const,
        label: WORK_VIEW_LABELS[view],
        render: (value: unknown, row: OrganizationBillingSummary) => (
          <Button
            component={RouterLink}
            to={adminClaimWorkUrl(row.organization_id, view)}
            onClick={event => event.stopPropagation()}
            onAuxClick={event => event.stopPropagation()}
            size="small"
            color={view === 'fix' ? 'error' : 'inherit'}
            aria-label={`${WORK_VIEW_LABELS[view]} ${Number(value ?? 0)} for ${row.organization_name ?? 'organization'}`}
          >
            {Number(value ?? 0)}
          </Button>
        ),
      })),
      {
        key: 'total_claims' as const,
        label: 'All claim work',
        render: (value: unknown, row: OrganizationBillingSummary) => (
          <Button
            component={RouterLink}
            to={adminClaimWorkUrl(row.organization_id, 'all_claim_work')}
            onClick={event => event.stopPropagation()}
            onAuxClick={event => event.stopPropagation()}
            size="small"
          >
            {Number(value ?? 0)}
          </Button>
        ),
      },
      {
        key: 'denial_rate_pct' as const,
        label: 'Historical denial %',
        render: (value: unknown) => {
          const pct = value as number | null | undefined;
          const color = pct != null && pct > 10 ? 'text-red-600 font-medium' : 'text-gray-900';
          return <span className={color}>{formatPercent(pct)}</span>;
        },
      },
      {
        key: 'collection_rate_pct' as const,
        label: 'Collection %',
        render: (value: unknown) => {
          const pct = value as number | null | undefined;
          const color = pct != null && pct >= 80 ? 'text-green-600' : 'text-gray-900';
          return <span className={color}>{formatPercent(pct)}</span>;
        },
      },
      {
        key: 'total_balance_cents' as const,
        label: 'Balance',
        render: (value: unknown) => (
          <span className="text-gray-900">{formatCents(value as number | null | undefined)}</span>
        ),
      },
      {
        key: 'claims_created_last_30d' as const,
        label: 'New (30d)',
        render: (value: unknown) => (
          <span className="text-gray-900">{String(Number(value || 0))}</span>
        ),
      },
      {
        key: 'unmatched_remittance_claims' as const,
        label: 'Unmatched',
        render: (value: unknown) => {
          const count = Number(value || 0);
          return count > 0 ? (
            <Chip
              label={String(count)}
              size="small"
              color="warning"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
          ) : (
            <span className="text-gray-400">0</span>
          );
        },
      },
    ],
    [],
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 py-8">
        <BackButton />

        <div className="flex items-center gap-3 mt-4 mb-6">
          <AccountBalance className="text-blue-600" fontSize="large" />
          <Typography variant="h4" className="font-bold text-gray-900">
            Billing Dashboard
          </Typography>
        </div>

        <FilterableDataTable<OrganizationBillingSummary>
          title="Organizations"
          columns={columns}
          data={data?.organizations ?? []}
          total={data?.total}
          loading={isLoading}
          page={page}
          perPage={perPage}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onPageChange={handlePageChange}
          onPerPageChange={handlePerPageChange}
          onFiltersChange={handleFiltersChange}
          onSortChange={handleSortChange}
          getRowUrl={row => adminClaimWorkUrl(row.organization_id, 'all_claim_work')}
          emptyMessage="No organizations with billing data found"
        />
      </main>
    </div>
  );
}
